import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import MapGL, { Source, Layer, Marker } from "react-map-gl/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import { Lightbulb, Loader2, X, Crosshair, ChevronRight, Lock, MapPin, Upload, Trash2, LandPlot, Building2, MonitorSmartphone, ZoomIn, ZoomOut, Eye, Sparkles, Store, Megaphone, Users, Wallet, Route, TrendingUp } from "lucide-react";
import { MAPBOX_TOKEN, MAP_STYLES, styleUrl, parseCoordinateInput } from "@landhep/map-engine";
import { postProScoredGrids } from "@landhep/api-client";
import { getBillingStatus, getOwnedCities, getCityPrice, openCity, skListAssets, skBulkAssets, skDeleteAsset, getGridFacts } from "../lib/api";
import { PotensiTypeView } from "../components/potensiReports";

const ASSET_META = {
  lahan:     { label: "Lahan",     icon: LandPlot,           color: "#16a34a" },
  gedung:    { label: "Gedung",    icon: Building2,          color: "#12805a" },
  videotron: { label: "Videotron", icon: MonitorSmartphone,  color: "#c026d3" },
};

const EMPTY = { type: "FeatureCollection", features: [] };
// Low→Best, same palette as the rest of the app.
const SCORE_COLORS = ["#ef4444", "#f97316", "#eab308", "#84cc16", "#4ade80", "#15803d"];
const TIERS = ["Rendah", "Cukup", "Sedang", "Bagus", "Tinggi", "Terbaik"];

function loadProfile() {
  try { return JSON.parse(localStorage.getItem("landhep_profile") || "{}"); }
  catch { return {}; }
}
const profileForFit = (p) => ({
  rentalBudget: p?.rentalBudget, target_ses: p?.target_ses,
  environments: p?.environments, avoidConditions: p?.avoidConditions,
  primaryCustomers: p?.primaryCustomers, ageGroups: p?.ageGroups,
  busyTimes: p?.busyTimes, priorities: p?.priorities, poiPriorities: p?.poiPriorities,
});

export default function Skenario() {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const mapLoaded = useRef(false);
  const pendingBounds = useRef(null);   // bounds to apply once the map is ready
  const fitTo = useCallback((bounds) => {
    if (mapRef.current && mapLoaded.current) {
      mapRef.current.fitBounds(bounds, { padding: { top: 90, left: 400, right: 420, bottom: 60 }, duration: 900 });
    } else {
      pendingBounds.current = bounds;   // map not ready yet → apply on load
    }
  }, []);
  const profile = useMemo(loadProfile, []);
  const targetSes    = profile.target_ses ?? "all";
  const resolution   = profile.gridResolution === "1km" ? "1km" : "100m";
  const radius       = profile.populationCatchment ?? "5km";

  const [city, setCity]         = useState(profile.city?.kode_kabkot ?? "");
  const [cityName, setCityName] = useState(profile.city?.nama_kabkot ?? "");
  const [cities, setCities]     = useState([]);        // {kodeKabkot, namaKabkot, owned}
  const [layer, setLayer]       = useState("potensi"); // potensi | populasi | poi

  const [view, setView] = useState({ longitude: 106.82, latitude: -6.2, zoom: 11 });
  const [mapStyle, setMapStyle] = useState("streets-v12");
  const [gridOpacity, setGridOpacity] = useState(0.5);
  const [showOpacity, setShowOpacity] = useState(false);
  const [rawGrid, setRawGrid] = useState(EMPTY);       // fetched cells (raw fields)
  const [gridLoading, setGridLoading] = useState(false);

  // City gate — the Potensi Lokasi heatmap is what a city purchase unlocks
  // (one purchase covers Analysis + Skenario). Scenario reports are free once open.
  const [billingOn, setBillingOn] = useState(false);
  const [cityOwned, setCityOwned] = useState(true);
  const [cityPrice, setCityPrice] = useState(null);
  const [unlocking, setUnlocking] = useState(false);
  const scoresLocked = billingOn && !cityOwned;

  const refreshCityLock = useCallback(async () => {
    try {
      const s = await getBillingStatus();
      setBillingOn(s.enabled);
      if (!s.enabled || !profile.id || !city) { setCityOwned(true); return; }
      const owned = await getOwnedCities(profile.id);
      const isOwned = owned.some((c) => c.kode_kabkot === city && c.is_active);
      setCityOwned(isOwned);
      if (!isOwned) { try { setCityPrice(await getCityPrice(city)); } catch { /* ignore */ } }
    } catch { setCityOwned(true); }
  }, [profile.id, city]);
  useEffect(() => { refreshCityLock(); }, [refreshCityLock]);

  async function unlockCity() {
    if (!profile.id || !city) return;
    setUnlocking(true);
    try { await openCity(profile.id, city); await refreshCityLock(); await loadCities(); }
    catch (e) { alert(e.message); } finally { setUnlocking(false); }
  }

  // City picker list: owned cities ∪ cities the user has assets in.
  const loadCities = useCallback(async () => {
    try {
      const [owned, myAssets] = await Promise.all([
        profile.id ? getOwnedCities(profile.id) : Promise.resolve([]),
        skListAssets().catch(() => []),
      ]);
      const map = new Map();
      for (const a of myAssets) if (a.kodeKabkot) map.set(a.kodeKabkot, { kodeKabkot: a.kodeKabkot, namaKabkot: a.namaKabkot || a.kodeKabkot, owned: false });
      for (const c of owned) if (c.is_active) map.set(c.kode_kabkot, { kodeKabkot: c.kode_kabkot, namaKabkot: c.nama_kabkot || c.kode_kabkot, owned: true });
      if (profile.city?.kode_kabkot && !map.has(profile.city.kode_kabkot))
        map.set(profile.city.kode_kabkot, { kodeKabkot: profile.city.kode_kabkot, namaKabkot: profile.city.nama_kabkot, owned: false });
      setCities([...map.values()].sort((a, b) => (a.namaKabkot || "").localeCompare(b.namaKabkot || "")));
    } catch { /* ignore */ }
  }, [profile.id]);
  useEffect(() => { loadCities(); }, [loadCities]);

  // Fetch the selected city's grid (business-agnostic signals). Only the raw fields
  // are stored; the displayed metric is chosen by `layer`.
  useEffect(() => {
    if (!city) { setRawGrid(EMPTY); return; }
    let cancelled = false;
    setGridLoading(true);
    postProScoredGrids(city, {
      subcategory: profile.bizSubtype ?? "", target_ses: targetSes,
      resolution, radius, profile: profileForFit(profile),
    })
      .then((d) => {
        if (cancelled) return;
        const fc = d?.features ? d : EMPTY;
        setRawGrid(fc);
        const lats = fc.features.map((f) => f.properties.lat).filter(Boolean);
        const lngs = fc.features.map((f) => f.properties.lng).filter(Boolean);
        if (lats.length) {
          fitTo([[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]]);
        }
      })
      .catch(() => { if (!cancelled) setRawGrid(EMPTY); })
      .finally(() => { if (!cancelled) setGridLoading(false); });
    return () => { cancelled = true; };
  }, [city, targetSes, resolution, radius]); // eslint-disable-line

  // The metric shown per cell, from the chosen layer.
  const metricOf = useCallback((p) => {
    if (layer === "populasi") return p.grid_pop_score ?? 0;
    if (layer === "poi") return p.poi_residual ?? null;   // OSM commercial vitality (z-ish)
    return Math.round(0.6 * (p.grid_pop_score ?? 0) + 0.4 * (p.grid_road_score ?? 0)); // potensi
  }, [layer]);

  // Colour the grid by the chosen metric, ranked per-city (recomputes on layer switch,
  // no refetch).
  const grid = useMemo(() => ({
    type: "FeatureCollection",
    features: rawGrid.features.map((f) => ({
      ...f, properties: { ...f.properties, _lval: metricOf(f.properties) },
    })),
  }), [rawGrid, metricOf]);

  const cuts = useMemo(() => {
    const s = grid.features
      .filter((f) => !f.properties.grid_filtered && f.properties._lval != null)
      .map((f) => f.properties._lval).sort((a, b) => a - b);
    if (s.length < 6) return [20, 40, 60, 80, 90];
    const q = (p) => s[Math.min(s.length - 1, Math.floor((s.length - 1) * p))];
    const c = [q(0.2), q(0.4), q(0.6), q(0.8), q(0.95)];
    for (let i = 1; i < c.length; i++) if (c[i] <= c[i - 1]) c[i] = c[i - 1] + 1e-6;
    return c;
  }, [grid]);

  const gridColor = useMemo(() => ["step", ["coalesce", ["get", "_lval"], -9999],
    SCORE_COLORS[0], cuts[0], SCORE_COLORS[1], cuts[1], SCORE_COLORS[2],
    cuts[2], SCORE_COLORS[3], cuts[3], SCORE_COLORS[4], cuts[4], SCORE_COLORS[5],
  ], [cuts]);

  // ── Click a grid → factual Profil Lokasi (free). Paid typed reports open in a popup. ──
  const [pin, setPin] = useState(null);            // {lat,lng}
  const [sel, setSel] = useState(null);            // { facts } | { error } for the clicked cell
  const [factsLoading, setFactsLoading] = useState(false);
  const [coordInput, setCoordInput] = useState("");
  const [popup, setPopup] = useState(null);        // { tab } when the report popup is open

  // Asset portfolio
  const [assets, setAssets] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [showAddCity, setShowAddCity] = useState(false);
  const loadAssets = useCallback(() => { skListAssets().then(setAssets).catch(() => setAssets([])); }, []);
  useEffect(() => { loadAssets(); }, [loadAssets]);

  // Open a location: show its factual profile (free). No charge, no recommendations.
  const openLocation = useCallback(async (lat, lng, asset = null) => {
    setPin({ lat, lng }); setSel(null); setPopup(null); setFactsLoading(true);
    try {
      const r = await getGridFacts(lat, lng);
      setSel(r?.ok ? { facts: r.facts, asset } : { error: r?.error || "Tidak ada data di titik ini." });
    } catch (e) { setSel({ error: e.message }); }
    finally { setFactsLoading(false); }
  }, []);

  function handleMapClick(e) {
    if (scoresLocked) return;
    openLocation(e.lngLat.lat, e.lngLat.lng);
  }
  function handleCoordSubmit(e) {
    e.preventDefault();
    const p = parseCoordinateInput(coordInput);
    if (!p) return;
    mapRef.current?.flyTo({ center: [p.lng, p.lat], zoom: 15, duration: 800 });
    openLocation(p.lat, p.lng);
  }

  return (
    <div className="relative w-full h-screen overflow-hidden bg-slate-100">
      <div className="absolute inset-0">
        <MapGL ref={mapRef} {...view} onMove={(e) => setView(e.viewState)} onClick={handleMapClick}
          onLoad={() => { mapLoaded.current = true; if (pendingBounds.current) { fitTo(pendingBounds.current); pendingBounds.current = null; } }}
          style={{ width: "100%", height: "100%" }} mapStyle={styleUrl(mapStyle)}
          mapboxAccessToken={MAPBOX_TOKEN} cursor={scoresLocked ? "not-allowed" : "crosshair"}>
          {!scoresLocked && (
            <Source id="sk-grid" type="geojson" data={grid}>
              <Layer id="sk-grid-fill" type="fill" source="sk-grid"
                filter={["!", ["get", "grid_filtered"]]}
                paint={{ "fill-color": gridColor, "fill-opacity": gridOpacity }} />
            </Source>
          )}
          {/* Asset portfolio markers */}
          {!scoresLocked && assets.filter((a) => a.lat != null && a.lng != null).map((a) => {
            const m = ASSET_META[a.assetType] || ASSET_META.lahan;
            return (
              <Marker key={a.id} longitude={a.lng} latitude={a.lat} anchor="center"
                onClick={(e) => { e.originalEvent.stopPropagation(); mapRef.current?.flyTo({ center: [a.lng, a.lat], zoom: 15, duration: 700 }); openLocation(a.lat, a.lng, a); }}>
                <div className="group relative flex flex-col items-center cursor-pointer" title={a.name}>
                  <div className="w-3.5 h-3.5 rounded-md border-2 border-white shadow" style={{ backgroundColor: m.color }} />
                  <div className="pointer-events-none absolute bottom-full mb-1 hidden group-hover:block whitespace-nowrap rounded-md bg-slate-900/90 px-2 py-1 text-[10px] font-semibold text-white z-10">
                    {a.name} · {m.label}
                  </div>
                </div>
              </Marker>
            );
          })}
          {pin && (
            <Marker longitude={pin.lng} latitude={pin.lat} anchor="bottom">
              <MapPin className="w-7 h-7 text-slate-900 drop-shadow" fill="#111" />
            </Marker>
          )}
        </MapGL>
      </div>

      {/* Header */}
      <div className="absolute top-[84px] left-[100px] right-3 z-20 flex items-start justify-between gap-3 pointer-events-none">
        <div className="pointer-events-auto flex items-center gap-2">
          <div className="flex items-center gap-2 bg-white/95 backdrop-blur rounded-2xl shadow-sm border border-slate-100 px-3 py-2">
            <Lightbulb className="w-5 h-5 text-amber-500" />
            <div>
              <p className="text-sm font-extrabold text-slate-800 leading-none">Skenario</p>
              <p className="text-[10px] text-slate-400 mt-0.5">Klik lokasi → profil & potensi grid</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 bg-white/95 backdrop-blur rounded-xl shadow-sm border border-slate-100 px-2.5 py-2">
            <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <select value={city}
              onChange={(e) => { const c = cities.find((x) => x.kodeKabkot === e.target.value); setCity(e.target.value); setCityName(c?.namaKabkot || ""); }}
              className="text-xs font-bold text-slate-700 bg-transparent outline-none max-w-[160px]">
              {cities.length === 0 && <option value="">Pilih kota…</option>}
              {cities.map((c) => <option key={c.kodeKabkot} value={c.kodeKabkot}>{c.namaKabkot}{c.owned ? "" : " 🔒"}</option>)}
            </select>
            <button onClick={() => setShowAddCity(true)} title="Tambah kota"
              className="shrink-0 text-amber-600 hover:text-amber-700 font-bold text-sm leading-none px-1">+</button>
          </div>
        </div>
        <div className="pointer-events-auto flex items-center gap-2">
          <form onSubmit={handleCoordSubmit} className="flex items-center gap-1.5 bg-white rounded-2xl p-2 shadow-md w-[280px]">
            <Crosshair className="w-3.5 h-3.5 text-gray-400 shrink-0 ml-1" />
            <input value={coordInput} onChange={(e) => setCoordInput(e.target.value)}
              placeholder="Koordinat atau URL Google Maps"
              className="flex-1 min-w-0 text-[11px] text-gray-700 placeholder-gray-400 bg-transparent outline-none py-1" />
            <button type="submit" disabled={!coordInput.trim()}
              className="shrink-0 px-2.5 py-1 rounded-lg bg-amber-500 text-white text-[11px] font-semibold hover:bg-amber-600 disabled:opacity-40">Go</button>
          </form>
          <button onClick={() => setShowUpload(true)}
            className="flex items-center gap-1.5 bg-white/95 backdrop-blur rounded-xl shadow-sm border border-slate-100 px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50">
            <Upload className="w-3.5 h-3.5" /> Aset CSV
          </button>
        </div>
      </div>

      {/* Left: asset portfolio */}
      <div className="absolute top-[140px] left-[100px] bottom-3 z-10 w-[280px] flex flex-col bg-white/95 backdrop-blur rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between shrink-0">
          <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Aset Saya</span>
          <span className="text-[10px] font-bold text-amber-600">{assets.length}</span>
        </div>
        <div className="flex-1 overflow-y-auto">
          {assets.length === 0 ? (
            <div className="px-5 py-10 text-center">
              <p className="text-xs font-bold text-slate-500">Belum ada aset</p>
              <p className="text-[10px] text-slate-400 mt-1 leading-relaxed">Unggah CSV aset (nama, tipe, lat, lng) atau klik lokasi mana pun di peta untuk memindai.</p>
              <button onClick={() => setShowUpload(true)} className="mt-3 text-[11px] font-bold text-amber-600 hover:text-amber-700">Unggah CSV →</button>
            </div>
          ) : assets.map((a) => {
            const m = ASSET_META[a.assetType] || ASSET_META.lahan;
            return (
              <div key={a.id} className="group flex items-center gap-2 px-4 py-2.5 border-b border-slate-50 hover:bg-amber-50/40">
                <button className="flex items-center gap-2 flex-1 min-w-0 text-left"
                  onClick={() => { mapRef.current?.flyTo({ center: [a.lng, a.lat], zoom: 15, duration: 700 }); openLocation(a.lat, a.lng, a); }}>
                  <span className="w-2.5 h-2.5 rounded-md shrink-0" style={{ backgroundColor: m.color }} />
                  <span className="min-w-0">
                    <span className="block text-[13px] font-bold text-slate-700 truncate">{a.name}</span>
                    <span className="block text-[10px] text-slate-400 truncate">{m.label}{a.namaKabkot ? ` · ${a.namaKabkot}` : ""}</span>
                  </span>
                </button>
                <button onClick={async () => { await skDeleteAsset(a.id); loadAssets(); }}
                  className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            );
          })}
        </div>
      </div>

      {showUpload && <AssetUploadModal onClose={() => setShowUpload(false)} onDone={() => { setShowUpload(false); loadAssets(); }} />}
      {showAddCity && <CityAddModal onClose={() => setShowAddCity(false)} onPick={(c) => { setCity(c.kode_kabkot); setCityName(c.nama_kabkot); setShowAddCity(false); }} />}

      {/* Legend */}
      {/* Map controls (top-right) — hidden while the Profil Lokasi panel occupies that corner. */}
      {!(factsLoading || sel) && (
        <div className="absolute top-[140px] right-3 z-20 flex flex-col gap-2 items-end pointer-events-none">
          <div className="pointer-events-auto bg-white rounded-2xl p-1.5 flex flex-col gap-0.5 shadow-md">
            <button onClick={() => mapRef.current?.flyTo({ zoom: mapRef.current.getZoom() + 1, duration: 300 })}
              className="flex items-center gap-1.5 px-3 py-2 hover:bg-gray-50 rounded-xl transition-colors">
              <ZoomIn className="w-3.5 h-3.5 text-gray-500" /><span className="text-xs font-semibold text-gray-500">Perbesar</span>
            </button>
            <button onClick={() => mapRef.current?.flyTo({ zoom: mapRef.current.getZoom() - 1, duration: 300 })}
              className="flex items-center gap-1.5 px-3 py-2 hover:bg-gray-50 rounded-xl transition-colors">
              <ZoomOut className="w-3.5 h-3.5 text-gray-500" /><span className="text-xs font-semibold text-gray-500">Perkecil</span>
            </button>
            <div className="h-px bg-gray-100 mx-2" />
            <div className="relative">
              <button onClick={() => setShowOpacity((v) => !v)}
                className="flex items-center gap-1.5 px-3 py-2 hover:bg-gray-50 rounded-xl transition-colors w-full">
                <Eye className="w-3.5 h-3.5 text-gray-500" /><span className="text-xs font-semibold text-gray-500">Opasitas</span>
              </button>
              {showOpacity && (
                <div className="absolute right-full mr-2 top-0 bg-white rounded-xl shadow-lg border border-slate-200 px-3 py-3 flex flex-col gap-1 min-w-[180px]">
                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Grid</span>
                  <div className="flex items-center gap-2">
                    <input type="range" min={0.1} max={1} step={0.05} value={gridOpacity}
                      onChange={(e) => setGridOpacity(Number(e.target.value))}
                      className="flex-1 h-1 cursor-pointer accent-amber-500" />
                    <span className="text-[10px] font-mono text-slate-500 w-7 text-right">{Math.round(gridOpacity * 100)}%</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="pointer-events-auto bg-white rounded-2xl p-2.5 shadow-md flex flex-col gap-1">
            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-0.5 px-1">Peta dasar</p>
            {MAP_STYLES.map((style) => {
              const active = mapStyle === style.id;
              return (
                <button key={style.id} onClick={() => setMapStyle(style.id)}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-xl text-left transition-colors ${active ? "bg-[#e3eae8]" : "hover:bg-gray-50"}`}>
                  <span className="w-4 h-4 rounded-md shrink-0 border border-black/10" style={{ backgroundColor: style.color }} />
                  <span className={`text-[11px] font-semibold whitespace-nowrap ${active ? "text-[#14543e]" : "text-gray-500"}`}>{style.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {!scoresLocked && (
        <div className="absolute bottom-4 left-[396px] z-10 bg-white/95 backdrop-blur rounded-xl shadow-sm border border-slate-100 px-3 py-2">
          <div className="flex items-center gap-1 mb-1.5">
            <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Layer grid</span>
            {gridLoading && <Loader2 className="w-3 h-3 text-slate-300 animate-spin" />}
          </div>
          <div className="flex gap-1 mb-2">
            {[["potensi", "Potensi"], ["populasi", "Populasi"], ["poi", "Keramaian POI"]].map(([k, l]) => (
              <button key={k} onClick={() => setLayer(k)}
                className={`px-2 py-1 rounded-lg text-[10px] font-bold transition-colors ${layer === k ? "bg-amber-500 text-white" : "text-slate-500 hover:bg-slate-50"}`}>{l}</button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            {SCORE_COLORS.map((c, i) => <span key={i} className="w-5 h-2.5 rounded-sm" style={{ backgroundColor: c }} title={TIERS[i]} />)}
          </div>
          <p className="text-[9px] text-slate-400 mt-1">Rendah → Terbaik · relatif dalam kota ini</p>
        </div>
      )}

      {/* City lock */}
      {scoresLocked && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6 max-w-sm text-center">
            <div className="w-11 h-11 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-3"><Lock className="w-5 h-5 text-amber-500" /></div>
            <p className="text-sm font-bold text-slate-800">Buka {cityName || "kota ini"} untuk Skenario</p>
            <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">Satu pembelian kota membuka heatmap Analysis <b>dan</b> Skenario.</p>
            <button onClick={unlockCity} disabled={unlocking}
              className="mt-4 px-4 py-2 rounded-xl bg-amber-500 text-white text-xs font-bold hover:bg-amber-600 disabled:opacity-50 inline-flex items-center gap-2">
              {unlocking && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {cityPrice?.price != null ? `Buka kota · Rp ${Number(cityPrice.price).toLocaleString("id-ID")}` : "Buka kota"}
            </button>
          </div>
        </div>
      )}

      {/* Profil Lokasi panel (free facts) */}
      {(factsLoading || sel) && !scoresLocked && (
        <LokasiPanel loading={factsLoading} sel={sel}
          onClose={() => { setSel(null); setPin(null); }}
          onOpenReport={(tab) => setPopup({ tab })} />
      )}

      {/* Typed report popup (Klasik / Potensi Bisnis / Potensi Iklan) */}
      {popup && sel?.facts && (
        <PotensiPopup
          facts={sel.facts}
          asset={sel.asset}
          initialTab={popup.tab}
          hasBusiness={!!(profile.bizSubtype)}
          onClose={() => setPopup(null)}
          onOpenKlasik={() => {
            const f = sel.facts;
            // No hand-over token here: this button shows no price, so the report
            // page must quote first (its "see cost & run" card) before any paid run.
            navigate("/deep-analysis", { state: { cell: {
              gridId: String(f.grid_id), lat: f.lat, lng: f.lng,
              kode_kelurahan: f.region?.kode_kelurahan, kode_kabkot: f.region?.kode_kabkot,
              namaKabkot: f.region?.kabkot, namaKelurahan: f.region?.kelurahan,
              namaKecamatan: f.region?.kecamatan, namaProvinsi: f.region?.provinsi,
              subcategory: profile.bizSubtype, target_ses: targetSes, resolution,
            } } });
          }} />
      )}
    </div>
  );
}

// Confirm before opening a PAID Deep Analysis from the Skenario ranking.
// ── Profil Lokasi: free factual profile of the clicked grid (no recommendations) ──
const CATCH_RADII = [1, 3, 5, 7, 10, 15, 20, 25, 30, 35, 40];
const COHORTS = [
  ["pop_children", "Anak"], ["pop_youth", "Remaja"], ["pop_young_adult", "Dewasa muda"],
  ["pop_productive", "Produktif"], ["pop_middle_age", "Paruh baya"], ["pop_elderly", "Lansia"],
];
const fmtN = (n) => (n == null ? "—" : Math.round(n).toLocaleString("id-ID"));

function LokasiPanel({ loading, sel, onClose, onOpenReport }) {
  const [rk, setRk] = useState(5);
  const facts = sel?.facts;
  const region = facts?.region;
  const cat = facts?.catchment?.[rk] || {};
  const pop = cat.pop_total || 0;
  return (
    <div className="absolute top-[140px] right-3 bottom-3 z-20 w-[400px] max-h-[calc(100vh-156px)] flex flex-col bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-start justify-between gap-2 shrink-0">
        <div className="min-w-0">
          <p className="text-sm font-extrabold text-slate-800">Profil Lokasi</p>
          <p className="text-[10px] text-slate-400 truncate">
            {region ? [region.kelurahan, region.kecamatan, region.kabkot].filter(Boolean).join(", ") : "Memuat…"}
          </p>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 shrink-0"><X className="w-4 h-4" /></button>
      </div>

      {loading ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
          <p className="text-xs font-semibold">Memuat data lokasi…</p>
        </div>
      ) : sel?.error ? (
        <div className="flex-1 flex items-center justify-center px-6 text-center">
          <p className="text-xs text-slate-400">{sel.error}</p>
        </div>
      ) : facts ? (
        <div className="flex-1 overflow-y-auto">
          {/* Catchment population with radius tabs */}
          <div className="px-4 py-3 border-b border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400 flex items-center gap-1"><Users className="w-3 h-3" /> Populasi jangkauan</span>
            </div>
            <div className="flex flex-wrap gap-1 mb-2">
              {CATCH_RADII.map((r) => (
                <button key={r} onClick={() => setRk(r)}
                  className={`px-1.5 py-0.5 rounded text-[10px] font-bold transition-colors ${rk === r ? "bg-amber-500 text-white" : "bg-slate-50 text-slate-500 hover:bg-slate-100"}`}>{r}km</button>
              ))}
            </div>
            <p className="text-2xl font-extrabold text-slate-800 tabular-nums leading-none">{fmtN(pop)}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">jiwa dalam radius {rk} km</p>
            <div className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1">
              {COHORTS.map(([k, l]) => (
                <div key={k} className="flex items-center justify-between text-[11px]">
                  <span className="text-slate-500">{l}</span>
                  <span className="font-semibold text-slate-700 tabular-nums">{fmtN(cat[k])}</span>
                </div>
              ))}
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-slate-500">Perempuan</span>
                <span className="font-semibold text-slate-700 tabular-nums">{pop ? Math.round((cat.pop_female || 0) / pop * 100) : 0}%</span>
              </div>
            </div>
          </div>

          {/* Quick facts */}
          <div className="px-4 py-3 border-b border-slate-100 grid grid-cols-2 gap-3">
            <Fact icon={Wallet} label="Daya beli" value={facts.buying_power_index != null ? facts.buying_power_index : "—"}
              hint={facts.buying_power_index != null ? "100 = rata-rata nasional" : ""} />
            <Fact icon={Route} label="Akses jalan" value={facts.roads?.type_label ? cap(facts.roads.type_label) : (facts.roads?.exists ? "Ada" : "Terbatas")} />
          </div>

          {/* POI keramaian */}
          <div className="px-4 py-3 border-b border-slate-100">
            <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Keramaian sekitar (1 km)</p>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(facts.poi_context || {}).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([k, v]) => (
                <span key={k} className="text-[10px] rounded-full bg-slate-50 border border-slate-100 px-2 py-0.5 text-slate-600">{poiLabel(k)} <b className="text-slate-800">{v}</b></span>
              ))}
              {Object.keys(facts.poi_context || {}).length === 0 && <span className="text-[11px] text-slate-400">Data terbatas</span>}
            </div>
          </div>

          {/* Actions → paid typed reports */}
          <div className="px-4 py-3 space-y-2">
            <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Analisis mendalam</p>
            <button onClick={() => onOpenReport("potensi_bisnis")}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-emerald-50 hover:bg-emerald-100 transition-colors text-left">
              <Store className="w-4 h-4 text-emerald-600 shrink-0" />
              <span className="min-w-0"><span className="block text-[12px] font-bold text-emerald-800">Potensi Bisnis</span><span className="block text-[10px] text-emerald-600/80">Bisnis apa yang paling cocok di sini</span></span>
              <ChevronRight className="w-4 h-4 text-emerald-400 ml-auto shrink-0" />
            </button>
            <button onClick={() => onOpenReport("potensi_iklan")}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-fuchsia-50 hover:bg-fuchsia-100 transition-colors text-left">
              <Megaphone className="w-4 h-4 text-fuchsia-600 shrink-0" />
              <span className="min-w-0"><span className="block text-[12px] font-bold text-fuchsia-800">Potensi Iklan</span><span className="block text-[10px] text-fuchsia-600/80">Iklan apa yang cocok dipasang di sini</span></span>
              <ChevronRight className="w-4 h-4 text-fuchsia-400 ml-auto shrink-0" />
            </button>
            <button onClick={() => onOpenReport("klasik")}
              className="w-full text-center text-[11px] font-semibold text-slate-500 hover:text-slate-700 py-1">atau buka Analisis Klasik untuk bisnismu →</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Fact({ icon: Icon, label, value, hint }) {
  return (
    <div>
      <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</p>
      <p className="text-sm font-extrabold text-slate-800 mt-0.5">{value}</p>
      {hint && <p className="text-[9px] text-slate-400">{hint}</p>}
    </div>
  );
}
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const POI_LABEL = {
  office: "Kantor", government: "Pemerintah", campus: "Kampus", education: "Edukasi",
  transit: "Transit", transit_hub: "Terminal", mall: "Mal", supermarket: "Swalayan",
  convenience: "Minimarket", restaurant: "Kuliner", cafe: "Kafe", health: "Kesehatan", market: "Pasar",
};
const poiLabel = (k) => POI_LABEL[k] || k;

// ── Typed report popup (Klasik / Potensi Bisnis / Potensi Iklan) ─────────────────
const TABS = [
  { id: "potensi_bisnis", label: "Potensi Bisnis", icon: Store, color: "#059669" },
  { id: "potensi_iklan", label: "Potensi Iklan", icon: Megaphone, color: "#c026d3" },
  { id: "klasik", label: "Analisis Klasik", icon: TrendingUp, color: "#0f766e" },
];
const TAB_HEADER = {
  potensi_bisnis: { title: "Potensi Bisnis", sub: "Bisnis apa yang paling cocok di grid ini" },
  potensi_iklan:  { title: "Potensi Iklan", sub: "Iklan apa yang cocok dipasang di sini" },
  klasik:         { title: "Analisis Klasik", sub: "Untuk bisnis profil onboarding-mu" },
};

function PotensiPopup({ facts, asset, initialTab, hasBusiness, onClose, onOpenKlasik }) {
  const [tab, setTab] = useState(initialTab === "klasik" ? "klasik" : (initialTab || "potensi_bisnis"));
  const grid = { grid_id: facts.grid_id, lat: facts.lat, lng: facts.lng, region: facts.region };
  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" style={{ backgroundColor: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Tabs at the VERY top — each report type has its own header (no sitescore/fit). */}
        <div className="flex items-stretch justify-between border-b border-slate-100 shrink-0 pr-2">
          <div className="flex">
            {TABS.map((t) => {
              const active = tab === t.id;
              return (
                <button key={t.id} onClick={() => setTab(t.id)}
                  className={`flex items-center gap-1.5 px-4 py-3 text-[12px] font-bold border-b-2 transition-colors ${active ? "border-current" : "border-transparent text-slate-400 hover:text-slate-600"}`}
                  style={active ? { color: t.color } : undefined}>
                  <t.icon className="w-3.5 h-3.5" /> {t.label}
                </button>
              );
            })}
          </div>
          <button onClick={onClose} className="self-center text-slate-400 hover:text-slate-600 shrink-0 px-2"><X className="w-5 h-5" /></button>
        </div>
        {/* Per-tab header */}
        <div className="px-5 py-3 border-b border-slate-100 shrink-0">
          <p className="text-sm font-extrabold text-slate-800">{TAB_HEADER[tab]?.title}</p>
          <p className="text-[10px] text-slate-400 truncate">
            {TAB_HEADER[tab]?.sub} · {[facts.region?.kelurahan, facts.region?.kecamatan, facts.region?.kabkot].filter(Boolean).join(", ")}
          </p>
        </div>
        <div className="flex-1 overflow-y-auto">
          {tab === "klasik" ? (
            <div className="py-16 px-8 text-center">
              <TrendingUp className="w-8 h-8 text-brand-600 mx-auto mb-3" />
              <p className="text-sm font-bold text-slate-700 mb-1">Analisis Klasik</p>
              <p className="text-[12px] text-slate-500 leading-relaxed max-w-md mx-auto">Potensi grid ini untuk <b>bisnis profil onboarding</b> kamu — laporan lapangan lengkap dengan kompetitor, benchmark, dan skor kecocokan.</p>
              {hasBusiness ? (
                <button onClick={onOpenKlasik} className="mt-4 px-4 py-2.5 rounded-xl bg-brand-700 text-white text-xs font-bold hover:bg-brand-800">Buka Analisis Klasik →</button>
              ) : (
                <p className="mt-4 text-[11px] text-amber-700 bg-amber-50 rounded-lg px-3 py-2 inline-block">Perlu profil bisnis (onboarding) untuk analisis ini.</p>
              )}
            </div>
          ) : (
            <PotensiTypeView key={tab} type={tab} grid={grid} asset={asset} />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Add / switch city (search any city; unlock happens via the lock banner) ──────
const API_BASE = import.meta.env.VITE_API_URL || "";
function CityAddModal({ onClose, onPick }) {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  useEffect(() => {
    fetch(`${API_BASE}/api/kecamatan/cities`).then((r) => r.json()).then(setList).catch(() => setList([]));
  }, []);
  const filtered = q.length >= 2
    ? list.filter((c) => `${c.nama_provinsi} ${c.nama_kabkot}`.toLowerCase().includes(q.toLowerCase())).slice(0, 10)
    : [];
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="font-bold text-slate-800 text-sm">Pilih / tambah kota</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari kota / kabupaten…"
            className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-amber-400" />
          <div className="mt-2 max-h-64 overflow-y-auto">
            {filtered.map((c) => (
              <button key={c.kode_kabkot} onClick={() => onPick(c)}
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-amber-50 text-left border-b border-slate-50 last:border-0">
                <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <span className="min-w-0"><span className="block text-sm font-medium text-slate-700 truncate">{c.nama_kabkot}</span>
                  <span className="block text-[10px] text-slate-400 truncate">{c.nama_provinsi}</span></span>
              </button>
            ))}
            {q.length >= 2 && filtered.length === 0 && <p className="px-3 py-4 text-xs text-slate-400 text-center">Tidak ada hasil.</p>}
          </div>
          <p className="mt-2 text-[10px] text-slate-400 leading-relaxed">Pilih kota untuk melihat gridnya. Jika belum dimiliki, akan muncul opsi buka kota.</p>
        </div>
      </div>
    </div>
  );
}

// ── CSV upload for assets ──────────────────────────────────────────────────────
// Columns (header row): name, type, latitude, longitude [, notes]. type ∈
// lahan/gedung/videotron (default lahan).
function parseAssetCsv(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return { rows: [], errors: ["File kosong."] };
  const header = lines[0].split(",").map((h) => h.trim().toLowerCase());
  const idx = {
    name: header.findIndex((h) => ["name", "nama", "aset"].includes(h)),
    type: header.findIndex((h) => ["type", "tipe", "asset_type"].includes(h)),
    lat:  header.findIndex((h) => ["latitude", "lat"].includes(h)),
    lng:  header.findIndex((h) => ["longitude", "lng", "lon"].includes(h)),
    notes: header.findIndex((h) => ["notes", "catatan"].includes(h)),
    // Optional enrichment → prefills the generate popup (attrs).
    luas:   header.findIndex((h) => ["luas", "luas_m2", "size"].includes(h)),
    ruang:  header.findIndex((h) => ["tipe_ruang", "ruang"].includes(h)),
    format: header.findIndex((h) => ["format"].includes(h)),
    posisi: header.findIndex((h) => ["posisi", "position"].includes(h)),
    jam:    header.findIndex((h) => ["jam_tayang", "jam"].includes(h)),
  };
  const errors = [];
  if (idx.name < 0 || idx.lat < 0 || idx.lng < 0)
    return { rows: [], errors: ["Header wajib: name, latitude, longitude (opsional: type, notes, luas, tipe_ruang, format, posisi, jam_tayang)."] };
  const norm = (t) => { const v = (t || "").trim().toLowerCase(); return ["lahan", "gedung", "videotron"].includes(v) ? v : "lahan"; };
  const g = (c, i) => (i >= 0 ? (c[i] || "").trim() : "");
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const c = lines[i].split(",");
    const name = (c[idx.name] || "").trim();
    const lat = parseFloat(c[idx.lat]); const lng = parseFloat(c[idx.lng]);
    if (!name || !Number.isFinite(lat) || !Number.isFinite(lng)) { errors.push(`Baris ${i + 1} dilewati.`); continue; }
    const attrs = {};
    if (g(c, idx.luas)) attrs.luas_m2 = g(c, idx.luas);
    if (g(c, idx.ruang)) attrs.tipe_ruang = g(c, idx.ruang);
    if (g(c, idx.format)) attrs.format = g(c, idx.format);
    if (g(c, idx.posisi)) attrs.posisi = g(c, idx.posisi);
    if (g(c, idx.jam)) attrs.jam_tayang = g(c, idx.jam);
    rows.push({ name, assetType: idx.type >= 0 ? norm(c[idx.type]) : "lahan", lat, lng,
      notes: idx.notes >= 0 ? (c[idx.notes] || "").trim() : undefined,
      attrs: Object.keys(attrs).length ? attrs : undefined });
  }
  return { rows, errors };
}

function AssetUploadModal({ onClose, onDone }) {
  const [parsed, setParsed] = useState(null);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);

  function onFile(e) {
    const f = e.target.files?.[0]; if (!f) return;
    setFileName(f.name);
    const r = new FileReader();
    r.onload = () => setParsed(parseAssetCsv(String(r.result || "")));
    r.readAsText(f);
  }
  async function submit() {
    if (!parsed?.rows.length) return;
    setBusy(true);
    try { await skBulkAssets(parsed.rows); onDone(); }
    catch (e) { alert(e.message); setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="font-bold text-slate-800 text-sm">Unggah Aset (CSV)</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-[11px] text-slate-500 leading-relaxed">
            Kolom (dengan baris header): <b>name, type, latitude, longitude</b> (opsional: notes). Tipe: lahan / gedung / videotron.
          </p>
          <button onClick={() => {
            const csv = "name,type,latitude,longitude,notes\nLahan Sudirman,lahan,-6.2146,106.8451,contoh\nVideotron Thamrin,videotron,-6.1957,106.8230,";
            const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
            const a = document.createElement("a"); a.href = url; a.download = "template_aset.csv"; a.click(); URL.revokeObjectURL(url);
          }} className="text-[11px] font-bold text-amber-600 hover:text-amber-700">Unduh template →</button>
          <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-xl py-6 cursor-pointer hover:border-amber-300">
            <Upload className="w-5 h-5 text-slate-400" />
            <span className="text-xs font-semibold text-slate-500">{fileName || "Pilih file CSV"}</span>
            <input type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" />
          </label>
          {parsed && (
            <div className="text-[11px]">
              <p className="font-bold text-slate-700">{parsed.rows.length} aset siap diunggah{parsed.errors.length ? ` · ${parsed.errors.length} dilewati` : ""}.</p>
              <div className="mt-1 max-h-32 overflow-y-auto text-slate-500">
                {parsed.rows.slice(0, 6).map((r, i) => <div key={i} className="truncate">{r.name} · {r.assetType} · {r.lat}, {r.lng}</div>)}
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-xs font-bold text-slate-500 hover:bg-slate-50">Batal</button>
          <button onClick={submit} disabled={busy || !parsed?.rows.length}
            className="px-4 py-2 rounded-xl bg-amber-500 text-white text-xs font-bold hover:bg-amber-600 disabled:opacity-50 inline-flex items-center gap-2">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Unggah {parsed?.rows.length || ""} aset
          </button>
        </div>
      </div>
    </div>
  );
}
